/**
 * components/LocateControl.jsx — Iter 34 GPS On Plans.
 *
 * The single field-facing GPS surface. Google-Maps-style locate-me
 * affordance. Drawing-first; no engineering chrome.
 *
 * Three actions, one bottom-right cluster:
 *   1. LOCATE ME       — one-tap: enable GPS if needed, pan to position
 *                        (preserves zoom), show accuracy ring + status pill
 *   2. FOLLOW MODE     — toggle: keep position centered as the super walks
 *   3. DROP POSITION   — manual marker for crew coordination (no GPS req'd)
 *
 * Status pill shows at-a-glance:
 *   • "Locating…"        — geolocation request pending, no fix yet
 *   • "Active · ±4 m"    — GPS fix, ring shows reported accuracy
 *   • "Weak signal"      — fix received but accuracy > 50 m
 *   • "Searching"        — GPS enabled, browser still hunting
 *   • "Set anchors"      — calibration insufficient (≥2 needed)
 *   • "GPS off"          — toggle off (resting state)
 *   • "Unavailable"      — geolocation API blocked / denied
 *
 * Production rules (no demo-isms):
 *   - Real navigator.geolocation only — no mocks.
 *   - All buttons functional from first tap; no future-state UI.
 *   - Context preservation: Locate / Follow pan WITHOUT zooming.
 *   - The Drop Position mode is independent of GPS calibration so it
 *     works on uncalibrated sheets.
 */
import React, { useMemo } from "react";
import { Crosshair, Locate, MapPin } from "lucide-react";


/**
 * Derive the operational status pill from the live GPS state.
 * Kept as a pure function so the parent can also read the same
 * label (e.g. for the GpsPanel readout).
 */
export function gpsStatus({ gpsEnabled, ready, livePosition, liveError }) {
  if (!gpsEnabled) {
    return { code: "off", label: "GPS off", tone: "muted" };
  }
  if (liveError) {
    return { code: "error", label: "Unavailable", tone: "error" };
  }
  if (!ready) {
    return { code: "needs_anchors", label: "Set anchors", tone: "warn" };
  }
  if (!livePosition) {
    return { code: "searching", label: "Searching", tone: "warn" };
  }
  const acc = livePosition.accuracy;
  if (acc != null && acc > 50) {
    return { code: "weak", label: `Weak signal · ±${Math.round(acc)} m`, tone: "warn" };
  }
  if (acc != null) {
    return { code: "active", label: `Active · ±${Math.round(acc)} m`, tone: "ok" };
  }
  return { code: "active", label: "Active", tone: "ok" };
}


const TONE = {
  ok:    { color: "#10b981", soft: "rgba(16,185,129,0.12)" },
  warn:  { color: "#f59e0b", soft: "rgba(245,158,11,0.12)" },
  error: { color: "var(--st-escalated, #ef4444)", soft: "rgba(239,68,68,0.12)" },
  muted: { color: "var(--fop-text-3)", soft: "rgba(255,255,255,0.04)" },
};


export default function LocateControl({
  // GPS state (passed from SheetViewer)
  gpsEnabled,
  ready,           // sheet has ≥2 anchors AND transform built
  livePosition,    // {lat, lng, accuracy} | null
  liveError,
  // Follow mode (managed by SheetViewer)
  followMode,
  onToggleFollow,
  // Locate Me action — owner pans canvas to current position
  onLocateMe,
  // Drop position mode (next canvas tap places a manual marker)
  dropMode,
  onToggleDropMode,
  hasManual,
  onClearManual,
  // Calibration entry — opens the GpsPanel
  onOpenCalibration,
  // Layout — shift left when the right-side Inspector panel is open
  // so the cluster never disappears behind it.
  inspectorOpen = false,
}) {
  const status = useMemo(
    () => gpsStatus({ gpsEnabled, ready, livePosition, liveError }),
    [gpsEnabled, ready, livePosition, liveError],
  );

  // Locate Me does the maximum-helpful thing for the current state:
  //   - no calibration → opens the calibration panel
  //   - calibration ok → enables GPS (if off) + pans on next fix
  //   - already active → just pans now
  // Iter 147 discovery — clarify what Locate Me actually is. iPad GPS is
  // 3-10 m accuracy; this is operational orientation only, not project
  // survey-grade positioning (which would require project survey control).
  const locateHint =
    !ready ? "Set sheet anchors first · iPad GPS only (rough, not survey-grade)"
    : !gpsEnabled ? "Start GPS · iPad GPS only (rough, not survey-grade)"
    : !livePosition ? "Waiting for fix… iPad GPS only (rough, not survey-grade)"
    : followMode ? "Recenter · iPad GPS only (rough, not survey-grade)"
    : "Pan to my position · iPad GPS only (rough, not survey-grade)";

  return (
    <div
      className={`fixed bottom-10 z-30 flex flex-col items-end gap-1.5 pointer-events-none transition-[right] duration-200 ${
        inspectorOpen ? "right-[19rem]" : "right-3"
      }`}
      data-testid="locate-control"
      data-status={status.code}
    >
      {/* Iter 178.7 — GPS status pill deleted. The status is redundantly
          conveyed by:
            (1) the Locate button's active/dim state,
            (2) the OperationalStatusSpine SyncPill when disconnected, and
            (3) the SheetStatusBar's Online/Offline indicator.
          Removing the standalone pill kills the "three pills say the same
          thing" pileup the operator called out. */}

      {/* Iter 178.5 — Action cluster rebuilt HORIZONTALLY.
          Old vertical stack ate ~180px of drawing real estate; new row
          matches the elite tool-rail vocabulary (dark surface gradient,
          orange accent for active, tokened border) and sits in ~180w ×
          52h — a fraction of the previous footprint. */}
      <div
        className="pointer-events-auto flex items-center gap-1 p-1 rounded-lg border border-[var(--fop-border)] shadow-lg"
        style={{
          background: "linear-gradient(180deg, var(--fop-surface-2) 0%, var(--fop-surface) 100%)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <ActionBtn
          testid="locate-me-btn"
          label="Locate"
          title={locateHint}
          Icon={Locate}
          onClick={onLocateMe}
          active={status.code === "active"}
        />
        <ActionBtn
          testid="follow-mode-btn"
          label="Follow"
          title={followMode ? "Stop following (drawing won't recenter)" : "Follow my position (auto-recenter)"}
          Icon={Crosshair}
          onClick={onToggleFollow}
          active={followMode}
          disabled={!ready}
        />
        <ActionBtn
          testid="drop-position-btn"
          label="Drop"
          title={dropMode ? "Cancel drop" : hasManual ? "Move manual position" : "Drop a manual position"}
          Icon={MapPin}
          onClick={onToggleDropMode}
          active={dropMode}
        />
        {hasManual && !dropMode && (
          <button
            type="button"
            onClick={onClearManual}
            className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] px-2 self-stretch flex items-center"
            data-testid="clear-manual-position"
            title="Remove the dropped position"
          >
            Clear
          </button>
        )}
      </div>

      {/* Quiet calibration affordance — only when needed. Drawing-first
          rule: never blink an alert; just expose the next sensible move. */}
      {status.code === "needs_anchors" && (
        <button
          type="button"
          onClick={onOpenCalibration}
          className="pointer-events-auto px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fop-primary)] bg-[var(--fop-primary-soft)] hover:brightness-110 border border-[var(--fop-primary)]"
          data-testid="open-gps-calibration"
        >
          Set GPS anchors →
        </button>
      )}
    </div>
  );
}


function ActionBtn({ testid, label, title, Icon, onClick, active, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-0.5 w-11 h-11 rounded-md transition-colors ${
        active
          ? "bg-[var(--fop-primary)]/15 text-[var(--fop-primary)]"
          : "text-[var(--fop-text)] hover:bg-[var(--fop-surface-3)] hover:text-white"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
      data-testid={testid}
      data-active={active ? "1" : "0"}
      title={title}
      aria-pressed={active ? "true" : "false"}
      aria-label={label}
    >
      <Icon size={14} strokeWidth={active ? 2.5 : 2} />
      <span className="text-[9px] font-mono uppercase tracking-wider leading-none">{label}</span>
    </button>
  );
}
